import { MapPin, Phone, MessageCircle, Linkedin, Mail } from "lucide-react";
import { useSeo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { EnquiryForm } from "@/components/EnquiryForm";
import {
  PHONE,
  PHONE_DISPLAY,
  WHATSAPP_URL,
  LINKEDIN_URL,
  EMAIL,
  ADDRESS_AURANGABAD,
  ADDRESS_PUNE,
} from "@/data/content";

export default function ContactPage() {
  useSeo({
    title: "Contact Jaybhaye Realty Pvt. Ltd. | Aurangabad & Pune",
    description:
      "Reach Jaybhaye Realty Pvt. Ltd. — project enquiries, landowner / development partnerships, investor enquiries. Direct desk +91 86693 61526. Offices in Chhatrapati Sambhaji Nagar (Aurangabad) and Pune.",
    path: "/contact",
    keywords: "Jaybhaye Realty contact, Jaybhaye Realty Pune, Jaybhaye Realty Aurangabad phone",
  });

  return (
    <div data-testid="contact-page">
      <section className="py-24 border-b border-[#12281a]/15">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <span className="text-xs uppercase tracking-[0.3em] text-[#b38b47] font-bold block mb-6">Contact</span>
          <h1 className="font-monument text-4xl sm:text-6xl text-[#12281a] leading-[1.02] uppercase mb-6" data-testid="contact-headline">
            Connect with Leadership
          </h1>
          <p className="text-xl text-[#2c382f] font-light leading-snug max-w-3xl">
            Project enquiries, landowner partnerships and investor conversations — handled directly by our executive desk.
          </p>
        </div>
      </section>

      <section className="py-24 bg-white border-b border-[#12281a]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-8">
            <Reveal>
              <div className="border border-[#12281a]/15 p-8 bg-[#f7f5f0] space-y-6" data-testid="contact-details-card">
                <div className="flex items-start gap-4">
                  <Phone size={18} className="text-[#b38b47] mt-1 shrink-0" />
                  <div>
                    <span className="text-xs uppercase tracking-widest text-[#12281a]/60 block">Direct Desk</span>
                    <a href={`tel:+91${PHONE}`} className="text-lg font-semibold text-[#12281a] hover:text-[#b38b47] transition-colors" data-testid="contact-phone-link">
                      {PHONE_DISPLAY}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <MessageCircle size={18} className="text-[#b38b47] mt-1 shrink-0" />
                  <div>
                    <span className="text-xs uppercase tracking-widest text-[#12281a]/60 block">WhatsApp</span>
                    <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="text-sm text-[#12281a] hover:text-[#b38b47] transition-colors" data-testid="contact-whatsapp-link">
                      Message us on WhatsApp
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Mail size={18} className="text-[#b38b47] mt-1 shrink-0" />
                  <div>
                    <span className="text-xs uppercase tracking-widest text-[#12281a]/60 block">Email</span>
                    <a href={`mailto:${EMAIL}`} className="text-sm text-[#12281a] hover:text-[#b38b47] transition-colors" data-testid="contact-email-link">
                      {EMAIL}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Linkedin size={18} className="text-[#b38b47] mt-1 shrink-0" />
                  <div>
                    <span className="text-xs uppercase tracking-widest text-[#12281a]/60 block">LinkedIn</span>
                    <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" className="text-sm text-[#12281a] hover:text-[#b38b47] transition-colors break-all" data-testid="contact-linkedin-link">
                      linkedin.com/in/sachinjaybhaye
                    </a>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div className="border border-[#12281a]/15 p-8" data-testid="contact-offices-card">
                <span className="text-xs uppercase tracking-widest text-[#b38b47] font-semibold block mb-6">Our Offices</span>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <MapPin size={18} className="text-[#b38b47] mt-1 shrink-0" />
                    <div>
                      <span className="text-sm font-semibold text-[#12281a] block">Chhatrapati Sambhaji Nagar (Aurangabad)</span>
                      <span className="text-xs text-[#12281a]/60">{ADDRESS_AURANGABAD}</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <MapPin size={18} className="text-[#b38b47] mt-1 shrink-0" />
                    <div>
                      <span className="text-sm font-semibold text-[#12281a] block">Pune</span>
                      <span className="text-xs text-[#12281a]/60">{ADDRESS_PUNE}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={100}>
              <div className="bg-[#12281a] text-[#f7f5f0] p-2">
                <div className="p-6 pb-0">
                  <span className="text-xs uppercase tracking-[0.25em] text-[#b38b47] font-semibold block mb-2">
                    Executive Desk
                  </span>
                  <h2 className="text-2xl font-monument text-[#f7f5f0] mb-2">Send an Enquiry</h2>
                  <p className="text-xs text-[#f7f5f0]/60">
                    Share a few details and our leadership desk will connect with you directly.
                  </p>
                </div>
                <div className="p-6">
                  <EnquiryForm />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
